import { Container, paintChild, type ContainerOptions } from "../core/Container.js";
import type { Control } from "../core/Control.js";
import type { Desktop } from "../core/Desktop.js";
import { rectContains, type Point } from "../../common/geometry.js";
import type { RenderContext } from "../render/RenderContext.js";
import type { Theme } from "../themes/Theme.js";
import { marqueeOffset } from "./geometry.js";

export interface MarqueeOptions extends ContainerOptions {
  /** Pixels a second; negative runs left to right. */
  speed?: number;
  /** Space between one child and the next, and between the end of the strip and its start. */
  gap?: number;
  /** Stop while the pointer is over the strip, so what is under it can be read and clicked. */
  pauseOnHover?: boolean;
}

/**
 * An endless strip: its children in a row, sliding past and coming round again - a parade of
 * portraits, a ticker of names, a band of logos.
 *
 * The children are laid out once, left to right, each at its own width and centered in the
 * height. The strip is then *drawn* as many times as it takes to cover the box, offset by how far
 * it has traveled, so there is no gap at the seam and no child is ever cloned. Hit testing folds
 * the pointer back into the one real strip, so every copy on screen is clickable.
 */
export class Marquee extends Container {
  speed: number;
  pauseOnHover: boolean;
  #gap: number;
  #traveled = 0;
  #lastFrame = -1;
  #registeredWith: Desktop | null = null;

  constructor(options: MarqueeOptions = {}) {
    super({ width: 400, height: 120, ...options });
    this.speed = options.speed ?? 40;
    this.pauseOnHover = options.pauseOnHover ?? true;
    this.#gap = options.gap ?? 16;
  }

  get gap(): number {
    return this.#gap;
  }
  set gap(v: number) {
    this.#gap = v;
    this.layout();
    this.invalidate();
  }

  /** How long one pass of the strip is, gap included. */
  get period(): number {
    let total = 0;
    for (const c of this.controls) if (c.visible) total += c.width + this.#gap;
    return total;
  }

  /** Where the first copy of the strip is drawn, 0 or less. */
  get offset(): number {
    return marqueeOffset(this.#traveled, 1, this.period);
  }

  /** True while the pointer is over the strip and `pauseOnHover` holds it. */
  get paused(): boolean {
    if (!this.pauseOnHover) return false;
    const hovered = this.desktop?.input.hoveredControl ?? null;
    return hovered !== null && (hovered === this || this.contains(hovered));
  }

  protected override performLayout(): void {
    const client = this.clientRect;
    let x = 0;
    for (const c of this.controls) {
      if (!c.visible) continue;
      c.setBounds(x, Math.round((client.height - c.height) / 2), c.width, c.height);
      x += c.width + this.#gap;
    }
  }

  protected override onEnvironmentChanged(): void {
    this.#syncAnimator();
  }

  /** Advance the strip to the desktop's clock. */
  #advance(): void {
    const desktop = this.desktop;
    const now = desktop?.frameTime ?? 0;
    const dt = this.#lastFrame < 0 ? 0 : Math.min(0.1, Math.max(0, now - this.#lastFrame));
    this.#lastFrame = now;
    if (desktop?.reducedMotion || this.paused) return;
    this.#traveled += dt * this.speed;
    const period = this.period;
    if (period > 0) this.#traveled %= period;
  }

  protected override onPaint(): void {
    // Nothing of its own: a marquee is only ever the strip.
  }

  protected override paintChildren(rc: RenderContext, theme: Theme): void {
    this.#syncAnimator();
    this.#advance();
    const period = this.period;
    if (period <= 0) return;
    const r = this.clientRect;
    rc.pushClip(r);
    if (!rc.isClippedOut) {
      for (let start = this.offset; start < r.width; start += period) {
        rc.pushTransform(r.x + start, r.y, 1);
        for (const child of this.controls) {
          if (!child.visible) continue;
          rc.pushTransform(child.x, child.y, 1);
          paintChild(rc, theme, child);
          rc.popTransform();
        }
        rc.popTransform();
      }
    }
    rc.popClip();
  }

  override hitTest(p: Point): Control | null {
    if (!this.visible || !rectContains(this.localRect, p)) return null;
    const period = this.period;
    const r = this.clientRect;
    if (period > 0 && rectContains(r, p)) {
      const along = (((p.x - r.x - this.offset) % period) + period) % period;
      const folded = super.hitTest({ x: r.x + along, y: p.y });
      if (folded && folded !== this) return folded;
    }
    return this.hitTestSelf(p) ? this : null;
  }

  #syncAnimator(): void {
    const desktop = this.desktop;
    const want = desktop !== null && this.speed !== 0 && !desktop.reducedMotion;
    const target = want ? desktop : null;
    if (target === this.#registeredWith) return;
    this.#registeredWith?.removeAnimator(this);
    this.#registeredWith = target;
    target?.addAnimator(this);
  }
}
